import Modal from "react-bootstrap/Modal";
import React, { useMemo, useState } from "react";

export default function SessionStandingsModal({
    show,
    onClose,
    sortedStandings,
    sortPriority,
    setSortPriority,
    getAvailableOptions,
    THEME_COLOR
}) {
    const themeAlpha = (opacity) => `${THEME_COLOR}${opacity}`;
    const [search, setSearch] = useState("");

    const filteredStandings = useMemo(() => {
        if (!search.trim()) return sortedStandings;

        return sortedStandings.filter(p =>
            p.name.toLowerCase().includes(search.toLowerCase())
        );
    }, [search, sortedStandings]);
    return (
        <Modal
            show={show}
            onHide={onClose}
            size="lg"
            centered
            scrollable
            /* backdropFilter here creates that premium frosted look */
            style={{ backdropFilter: "blur(6px)" }}
            contentClassName="border-0 shadow-lg rounded-5 overflow-hidden"
        >
            {/* ================= HEADER ================= */}
            <Modal.Header closeButton className="border-0 pt-4 px-4 pb-2">
                <Modal.Title className="d-flex align-items-center">
                    <div
                        className="rounded-4 d-flex align-items-center justify-content-center me-3 shadow-sm"
                        style={{
                            width: 42,
                            height: 42,
                            backgroundColor: themeAlpha("15"),
                            border: `1px solid ${themeAlpha("20")}`
                        }}
                    >
                        <i
                            className="bi bi-trophy-fill fs-5"
                            style={{ color: THEME_COLOR }}
                        ></i>
                    </div>

                    <div>
                        <div className="h5 mb-0">Session Standings</div>
                        <div
                            className="text-muted fw-bold"
                            style={{ fontSize: "0.7rem", textTransform: 'uppercase', letterSpacing: '0.5px' }}
                        >
                            Live performance tracking
                        </div>
                    </div>
                </Modal.Title>
            </Modal.Header>

            {/* ================= BODY ================= */}
            <Modal.Body className="px-4 pb-4 pt-0 custom-scrollbar">

                {/* ===== STICKY SORT PRIORITY ===== */}
                <div
                    className="sticky-top pt-2 pb-3"
                    style={{ zIndex: 10, marginTop: '-2px' }}
                >
                    <div className="rounded-4 p-3 shadow-sm" style={{
                        background: "var(--surface-bg)",
                        border: "1px solid var(--border-color)"
                    }}>
                        <div className="d-flex align-items-center mb-2">
                            <i
                                className="bi bi-sliders2-vertical me-2"
                                style={{ color: THEME_COLOR }}
                            ></i>
                            <label
                                className="small text-muted text-uppercase"
                                style={{ letterSpacing: "0.5px", fontSize: '0.65rem' }}
                            >
                                Sort Priority
                            </label>
                        </div>

                        <div className="row g-2">
                            {["1st", "2nd", "3rd"].map((label, index) => (
                                <div className="col-4" key={index}>
                                    <div className="position-relative">
                                        <select
                                            className="form-select form-select-sm rounded-3 border-0 shadow-sm fw-bold ps-3"
                                            style={{ fontSize: "0.75rem", height: "38px", cursor: 'pointer' }}
                                            value={sortPriority[index]}
                                            onChange={(e) => {
                                                const updated = [...sortPriority];
                                                updated[index] = e.target.value;
                                                setSortPriority(updated);
                                            }}
                                        >
                                            {getAvailableOptions(index).map((opt) => (
                                                <option key={opt.value} value={opt.value}>
                                                    {opt.label}
                                                </option>
                                            ))}
                                        </select>
                                        <span
                                            className="position-absolute top-0 start-0 translate-middle-y ms-2 px-1 text-muted fw-bold"
                                            style={{ fontSize: '0.5rem', marginTop: '2px' }}
                                        >
                                            {label}
                                        </span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
                {/* ===== SEARCH ===== */}
                <div className="mb-3 px-1">
                    <div className="position-relative">
                        <i
                            className="bi bi-search position-absolute top-50 start-0 translate-middle-y ms-3 text-muted"
                            style={{ fontSize: "0.8rem" }}
                        ></i>

                        <input
                            type="text"
                            className="form-control rounded-pill ps-5 shadow-sm border-0"
                            placeholder="Search player..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            style={{
                                height: "38px",
                                fontSize: "0.85rem",
                                background: "var(--surface-bg)",
                                color: "var(--text-color)"
                            }}
                        />

                        {search && (
                            <button
                                className="btn btn-sm position-absolute top-50 end-0 translate-middle-y me-2"
                                onClick={() => setSearch("")}
                                style={{ fontSize: "0.7rem" }}
                            >
                                ✕
                            </button>
                        )}
                    </div>
                </div>

                {/* ===== STANDINGS TABLE ===== */}
                <div className="table-responsive">
                    <table className="table table-hover align-middle border-0">
                        <thead>
                            <tr
                                className="text-uppercase text-muted"
                                style={{ fontSize: "0.6rem", letterSpacing: "1px", fontWeight: '800' }}
                            >
                                <th className="border-0 ps-2">Rank</th>
                                <th className="border-0">Player</th>
                                <th className="border-0 text-center">W - L</th>
                                <th className="border-0 text-center">Pts</th>
                                <th className="border-0 text-center">Diff</th>
                                <th className="border-0 text-end pe-2">Win %</th>
                            </tr>
                        </thead>

                        <tbody className="border-0">
                            {filteredStandings.map((p, i) => {
                                const originalRank = sortedStandings.findIndex(
                                    s => s.name === p.name
                                ) + 1;
                                const isTopThree = originalRank <= 3;
                                const rankColors = ["#FFD700", "#C0C0C0", "#CD7F32"];

                                return (
                                    <tr key={p.name} className="border-white">
                                        <td className="ps-2">
                                            <span
                                                className={`badge rounded-circle d-flex align-items-center justify-content-center shadow-sm ${!isTopThree ? "text-muted border" : ""
                                                    }`}
                                                style={{
                                                    width: "28px",
                                                    height: "28px",
                                                    backgroundColor: isTopThree ? rankColors[originalRank-1] : "",
                                                    color: isTopThree ? "white" : "",
                                                    fontWeight: "900",
                                                    fontSize: '0.75rem'
                                                }}
                                            >
                                                {originalRank}
                                            </span>
                                        </td>

                                        <td>
                                            <div className="d-flex align-items-center">
                                                {/* PLAYER AVATAR */}
                                                <div className="flex-shrink-0 me-3">
                                                    {p.photoURL ? (
                                                        <img
                                                            src={p.photoURL}
                                                            alt={p.name}
                                                            className="rounded-circle shadow-sm border border-2 border-white"
                                                            style={{
                                                                width: "32px",
                                                                height: "32px",
                                                                objectFit: "cover"
                                                            }}
                                                            onError={(e) => {
                                                                e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(p.name)}&background=random`;
                                                            }}
                                                        />
                                                    ) : (
                                                        <div
                                                            className="rounded-circle d-flex align-items-center justify-content-center shadow-sm border border-2 border-white text-muted"
                                                            style={{
                                                                width: "32px",
                                                                height: "32px",
                                                                background: "var(--surface-bg)",
                                                                fontSize: "0.8rem"
                                                            }}
                                                        >
                                                            <i className="bi bi-person-fill"></i>
                                                        </div>
                                                    )}
                                                </div>

                                                {/* PLAYER NAME */}
                                                <div className="text-truncate" style={{ maxWidth: "150px" }}>
                                                    {p.name}
                                                </div>
                                            </div>
                                        </td>

                                        <td className="text-center">
                                            <div className="d-flex align-items-center justify-content-center gap-1">
                                                <span className="fw-bold">{p.wins}</span>
                                                <span className="text-muted opacity-50 small">/</span>
                                                <span className="text-muted small">{p.losses}</span>
                                            </div>
                                        </td>

                                        <td className="text-center fw-bold">
                                            {p.scored}
                                        </td>

                                        <td
                                            className={`text-center ${p.diff >= 0 ? "text-success" : "text-danger"
                                                }`}
                                        >
                                            {p.diff > 0 ? `+${p.diff}` : p.diff}
                                        </td>

                                        <td className="text-end pe-2">
                                            <span
                                                className="badge rounded-pill"
                                                style={{
                                                    backgroundColor: themeAlpha("10"),
                                                    color: THEME_COLOR,
                                                    fontSize: '0.8rem'
                                                }}
                                            >
                                                {Number(p.winRate).toFixed(0)}%
                                            </span>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </Modal.Body>

            {/* ================= FOOTER ================= */}
            <Modal.Footer className="border-0 pt-0 px-4 pb-4">
                <button
                    className="btn w-100 rounded-pill fw-bold py-2 border shadow-sm transition-all"
                    onClick={onClose}
                    style={{ fontSize: '0.9rem' }}
                >
                    <i className="bi bi-arrow-left me-2"></i>
                    Back to Courts
                </button>
            </Modal.Footer>

            <style>{`
        .fw-black { font-weight: 900; }
        .custom-scrollbar::-webkit-scrollbar { width: 4px; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: var(--surface-bg); border-radius: 10px; }
        .btn-close:focus { box-shadow: none; }
        .table > :not(caption) > * > * { padding: 0.85rem 0.5rem; border-bottom-width: 1px; border-color: var(--border-color); }
        .transition-all { transition: all 0.2s ease; }
        .transition-all:active { transform: scale(0.98); }
      `}</style>
        </Modal>
    );
}